import { create } from 'zustand';
import { EnteredByUser, AuthUser } from '../types';

/** Decode JWT payload (no signature verification — only used to drop expired sessions client-side). */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = decodeURIComponent(
      atob(b64 + pad)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** True if JWT is missing exp, unreadable, or past expiry (with small clock skew). */
function isPersistedJwtInvalidOrExpired(jwt: string, skewSeconds = 120): boolean {
  const payload = decodeJwtPayload(jwt);
  if (!payload) return true;
  if (typeof payload.exp !== 'number') return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + skewSeconds;
}

interface AuthState {
  jwt: string | null;
  authUser: AuthUser | null;
  // Legacy support
  enteredBy: EnteredByUser | null;
  
  setJwt: (jwt: string) => void;
  setAuthUser: (user: AuthUser) => void;
  setEnteredBy: (user: EnteredByUser) => void; // Legacy - for admin
  logout: () => void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  isContractor: () => boolean;
  isEmployee: () => boolean;
}

// Simple localStorage-based persistence
const getStoredState = (): Pick<AuthState, 'jwt' | 'authUser' | 'enteredBy'> => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored) as Pick<AuthState, 'jwt' | 'authUser' | 'enteredBy'>;
        if (parsed.jwt && isPersistedJwtInvalidOrExpired(parsed.jwt)) {
          // Matches server verifyToken expiry — avoids HTTPS calls with dead JWT (push, alerts) and spurious logouts.
          console.info('[auth] Persisted session expired or invalid; clearing stored credentials.');
          localStorage.removeItem('auth-storage');
          return { jwt: null, authUser: null, enteredBy: null };
        }
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load auth from storage', e);
  }
  return { jwt: null, authUser: null, enteredBy: null };
};

const persistState = (state: Pick<AuthState, 'jwt' | 'authUser' | 'enteredBy'>) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('auth-storage', JSON.stringify(state));
    }
  } catch (e) {
    console.error('Failed to save auth to storage', e);
  }
};

const initialState = getStoredState();

export const useAuthStore = create<AuthState>((set, get) => ({
  jwt: initialState.jwt,
  authUser: initialState.authUser,
  enteredBy: initialState.enteredBy,
  
  setJwt: (jwt) => {
    set({ jwt });
    persistState({ jwt, authUser: get().authUser, enteredBy: get().enteredBy });
  },
  
  setAuthUser: (user) => {
    set({ authUser: user });
    // For backwards compatibility with admin users
    if (user.userType === 'admin' && user.enteredBy) {
      set({ enteredBy: user.enteredBy });
    }
    persistState({ jwt: get().jwt, authUser: user, enteredBy: get().enteredBy });
  },
  
  setEnteredBy: (user) => {
    set({ enteredBy: user });
    // Also update authUser for backwards compatibility
    set({ authUser: { userType: 'admin', enteredBy: user } });
    persistState({ jwt: get().jwt, authUser: get().authUser, enteredBy: user });
  },
  
  logout: () => {
    set({ jwt: null, authUser: null, enteredBy: null });
    persistState({ jwt: null, authUser: null, enteredBy: null });
  },
  
  isAuthenticated: () => {
    const { jwt, authUser } = get();
    return !!jwt && !!authUser;
  },
  
  isAdmin: () => {
    const { authUser } = get();
    return authUser?.userType === 'admin';
  },
  
  isContractor: () => {
    const { authUser } = get();
    return authUser?.userType === 'contractor';
  },
  
  isEmployee: () => {
    const { authUser } = get();
    return authUser?.userType === 'employee';
  },
}));

